/**
 * React cam: opt-in front camera + MediaRecorder. Records only between start() and stop(),
 * and the UI shows a REC indicator for exactly that window. Marks every game event that
 * arrives while recording (studio/marks.ts).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { MAX_RECORDING_MS, addMark, finishMarks, startMarks, type Mark, type MarkBook } from './marks';

const CANDIDATES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4;codecs=avc1,mp4a',
  'video/mp4',
];

/** The best container this browser can record (webm on Chromium/Firefox, mp4 on Safari). */
export function pickRecorderMime(isSupported: (m: string) => boolean = (m) => MediaRecorder.isTypeSupported?.(m) ?? false): string | undefined {
  try {
    return CANDIDATES.find((m) => isSupported(m));
  } catch {
    return undefined;
  }
}

export type Recording = {
  blob: Blob;
  url: string;
  mimeType: string;
  durationMs: number;
  marks: Mark[];
  startedAtSeq: number;
  endedAtSeq: number;
};

export type CamPhase = 'off' | 'asking' | 'denied' | 'ready' | 'recording' | 'review';

export function useReactCam() {
  const [phase, setPhase] = useState<CamPhase>('off');
  const [stream, setStream] = useState<MediaStream>();
  const [elapsed, setElapsed] = useState(0);
  const [limitMs, setLimitMs] = useState(MAX_RECORDING_MS);
  const [recording, setRecording] = useState<Recording>();
  const [markCount, setMarkCount] = useState(0);
  const rec = useRef<MediaRecorder>();
  const book = useRef<MarkBook>();
  const lastSeq = useRef(-1);
  const t0 = useRef(0);
  const timer = useRef<ReturnType<typeof setInterval>>();
  const streamRef = useRef<MediaStream>();
  streamRef.current = stream;

  const enable = useCallback(async (): Promise<boolean> => {
    if (streamRef.current) return true;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setPhase('denied');
      return false;
    }
    setPhase('asking');
    try {
      const video = { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 1280 } };
      const s = await navigator.mediaDevices.getUserMedia({ video, audio: true }).catch(() => navigator.mediaDevices.getUserMedia({ video }));
      streamRef.current = s; // start() may run before the next render
      setStream(s);
      setPhase('ready');
      return true;
    } catch {
      setPhase('denied');
      return false;
    }
  }, []);

  const stop = useCallback(() => {
    const r = rec.current;
    if (r && r.state !== 'inactive') r.stop();
    clearInterval(timer.current);
  }, []);

  const start = useCallback(
    (startedAtSeq: number, maxMs = MAX_RECORDING_MS) => {
      const s = streamRef.current;
      if (!s || rec.current?.state === 'recording') return;
      const mime = pickRecorderMime();
      const r = new MediaRecorder(s, mime ? { mimeType: mime } : undefined);
      const chunks: Blob[] = [];
      r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      r.onstop = () => {
        const durationMs = Math.min(maxMs, Math.round(performance.now() - t0.current));
        const type = (r.mimeType || mime || 'video/webm').split(';')[0]!;
        const blob = new Blob(chunks, { type });
        const b = book.current!;
        setRecording({
          blob,
          url: URL.createObjectURL(blob),
          mimeType: type,
          durationMs,
          marks: finishMarks(b, durationMs),
          startedAtSeq: b.startedAtSeq,
          endedAtSeq: Math.max(b.startedAtSeq, lastSeq.current),
        });
        setPhase('review');
      };
      t0.current = performance.now();
      book.current = startMarks(t0.current, startedAtSeq);
      lastSeq.current = startedAtSeq;
      setMarkCount(0);
      setElapsed(0);
      setLimitMs(maxMs);
      rec.current = r;
      r.start(500);
      setPhase('recording');
      timer.current = setInterval(() => {
        const e = performance.now() - t0.current;
        setElapsed(e);
        if (e >= maxMs) stop();
      }, 200);
    },
    [stop],
  );

  /** Called for every game event that arrives (only counts while recording). */
  const mark = useCallback((seq: number) => {
    if (rec.current?.state !== 'recording' || !book.current) return;
    lastSeq.current = Math.max(lastSeq.current, seq);
    const next = addMark(book.current, seq, performance.now());
    if (next !== book.current) {
      book.current = next;
      setMarkCount(next.marks.length);
    }
  }, []);

  const discard = useCallback(() => {
    if (recording) URL.revokeObjectURL(recording.url);
    setRecording(undefined);
    setPhase(streamRef.current ? 'ready' : 'off');
  }, [recording]);

  /** Published: keep the blob (the reel needs it) but leave review. */
  const keep = useCallback(() => setPhase(streamRef.current ? 'ready' : 'off'), []);

  const disable = useCallback(() => {
    stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setStream(undefined);
    setPhase('off');
  }, [stop]);

  useEffect(
    () => () => {
      clearInterval(timer.current);
      if (rec.current && rec.current.state !== 'inactive') rec.current.stop();
      streamRef.current?.getTracks().forEach((t) => t.stop());
    },
    [],
  );

  return { phase, stream, elapsed, limitMs, markCount, recording, enable, start, stop, mark, discard, keep, disable };
}

export type ReactCam = ReturnType<typeof useReactCam>;
